import React, { useState } from 'react';
import { Box, Typography, Collapse, IconButton } from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';

interface ChainOfThoughtProps {
  steps: string[];
  defaultExpanded?: boolean;
}

/**
 * Parse a reasoning step to extract a title (bold text at start) and body.
 * Looks for patterns like "**Searching for...**" or "**Analyzing...**"
 */
function parseStep(step: string): { title: string | null; body: string } {
  // Match **Title** at the beginning of the step
  const boldMatch = step.match(/^\*\*(.+?)\*\*\s*(.*)/s);
  if (boldMatch) {
    return {
      title: boldMatch[1].trim(),
      body: boldMatch[2].trim(),
    };
  }

  // Match a short first sentence as title (ending with : or .)
  const sentenceMatch = step.match(/^([^.:\n]{1,60}[.:])\s*(.*)/s);
  if (sentenceMatch && sentenceMatch[2].length > 20) {
    return {
      title: sentenceMatch[1].trim(),
      body: sentenceMatch[2].trim(),
    };
  }

  // No title found
  return { title: null, body: step };
}

export default function ChainOfThought({ steps, defaultExpanded = false }: ChainOfThoughtProps) {
  const [expanded, setExpanded] = useState(defaultExpanded);

  if (!steps || steps.length === 0) {
    return null;
  }

  return (
    <Box>
      {/* Collapsed header / toggle */}
      <Box
        onClick={() => setExpanded(!expanded)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          cursor: 'pointer',
          '&:hover': { opacity: 0.8 },
        }}
      >
        {expanded ? <ExpandLessIcon sx={{ fontSize: 14, color: 'text.secondary' }} /> : <ExpandMoreIcon sx={{ fontSize: 14, color: 'text.secondary' }} />}
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
          Chain of Thought ({steps.length})
        </Typography>
      </Box>

      {/* Expanded content */}
      <Collapse in={expanded}>
        <Box sx={{ position: 'relative', pl: 3, pt: 0.5 }}>
          {/* Vertical connecting line */}
          <Box
            sx={{
              position: 'absolute',
              left: 7,
              top: 12,
              bottom: 8,
              width: 1.5,
              bgcolor: 'primary.light',
              borderRadius: 1,
            }}
          />

          {steps.map((step, idx) => {
            const { title, body } = parseStep(step);
            const isLast = idx === steps.length - 1;

            return (
              <Box
                key={idx}
                sx={{
                  position: 'relative',
                  pb: isLast ? 0 : 1,
                  mb: isLast ? 0 : 0.5,
                }}
              >
                {/* Step dot */}
                <Box
                  sx={{
                    position: 'absolute',
                    left: -20,
                    top: 2,
                    width: 16,
                    height: 16,
                    borderRadius: '50%',
                    bgcolor: 'primary.main',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.55rem',
                    fontWeight: 700,
                    zIndex: 1,
                  }}
                >
                  {idx + 1}
                </Box>

                {/* Step content */}
                <Box
                  sx={{
                    bgcolor: 'background.paper',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 0.5,
                    p: 0.75,
                  }}
                >
                  {title && (
                    <Typography
                      sx={{
                        color: 'primary.main',
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        mb: body ? 0.25 : 0,
                      }}
                    >
                      {title}
                    </Typography>
                  )}
                  {body && (
                    <Typography
                      sx={{
                        color: 'text.secondary',
                        whiteSpace: 'pre-wrap',
                        lineHeight: 1.4,
                        fontSize: '0.7rem',
                      }}
                    >
                      {body}
                    </Typography>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
      </Collapse>
    </Box>
  );
}
